import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { PasswordField } from '../../components/ui/PasswordField'

interface EmagSettings {
  username: string | null
  passwordSet: boolean
  enabled: boolean
  lastSyncAt: string | null
  lastSyncCount: number | null
  updatedAt: string
}

interface SettingsResp {
  settings: EmagSettings | null
}

export function SetariEmagPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.emag:write')
  const canSync = permissions.includes('emag:write')

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [enabled, setEnabled] = useState(false)

  const query = useQuery<SettingsResp>({
    queryKey: ['emag-settings'],
    queryFn: async () => (await api.get<SettingsResp>('/emag/settings')).data,
    refetchInterval: 60_000,
  })

  const s = query.data?.settings
  const lastSync = s?.lastSyncAt ? new Date(s.lastSyncAt) : null

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { enabled }
      if (username.trim()) body.username = username.trim()
      if (password.trim()) body.password = password.trim()
      return (await api.put('/emag/settings', body)).data
    },
    onSuccess: () => {
      toast.success('Setările eMAG au fost salvate')
      queryClient.invalidateQueries({ queryKey: ['emag-settings'] })
      setPassword('')
    },
    onError: showError,
  })

  const testMutation = useMutation({
    mutationFn: async () => (await api.post('/emag/test-connection')).data,
    onSuccess: (d: unknown) => {
      const r = d as { ok: boolean; sampleCategories?: number; error?: string }
      if (r.ok) toast.success(`Conexiune OK (${r.sampleCategories ?? 0} categorii facturi)`)
      else toast.error(r.error ?? 'Eroare')
    },
    onError: showError,
  })

  const syncOrders = useMutation({
    mutationFn: async () => (await api.post('/emag/sync-orders')).data,
    onSuccess: (d: unknown) => {
      const r = d as { fetched?: number; upserted?: number }
      toast.success(`Comenzi: ${r.upserted ?? 0} actualizate din ${r.fetched ?? 0} citite`)
    },
    onError: showError,
  })

  const syncCustomerInv = useMutation({
    mutationFn: async () => (await api.post('/emag/sync-customer-invoices')).data,
    onSuccess: (d: unknown) => {
      const r = d as { fetched?: number; upserted?: number }
      toast.success(
        `Facturi de vânzare: ${r.upserted ?? 0} actualizate din ${r.fetched ?? 0} citite`,
      )
    },
    onError: showError,
  })

  const syncSupplierInv = useMutation({
    mutationFn: async () => (await api.post('/emag/sync-supplier-invoices')).data,
    onSuccess: (d: unknown) => {
      const r = d as { fetched?: number; upserted?: number }
      toast.success(
        `Facturi de cumpărare: ${r.upserted ?? 0} actualizate din ${r.fetched ?? 0} citite`,
      )
    },
    onError: showError,
  })

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări → eMAG — setări și sincronizare</h1>

      <Card title="Starea conexiunii">
        {s ? (
          <div className="space-y-2 text-sm">
            <div>
              <strong>Utilizator:</strong> <span className="font-mono">{s.username ?? '—'}</span>
            </div>
            <div>
              <strong>Parola:</strong> {s.passwordSet ? '✓ configurată' : '✗ nu e setată'}
            </div>
            <div>
              <strong>Activ:</strong> {s.enabled ? '✓ da' : '✗ nu'}
            </div>
            <div className="text-xs text-b4l-text-muted">
              Ultima sincronizare:{' '}
              {lastSync
                ? `${lastSync.toLocaleString('en-GB')} (${s.lastSyncCount ?? 0} comenzi)`
                : 'niciodată'}
            </div>
          </div>
        ) : (
          <p className="text-sm text-b4l-text-muted">Nu există setări salvate.</p>
        )}
      </Card>

      <Card
        title="Configurare API eMAG"
        description="Conectarea la eMAG Marketplace se face cu utilizatorul și parola de API. Le obții din eMAG Marketplace (Seller Center) → Setări cont → API."
      >
        <div className="space-y-3">
          <Field
            label="Utilizator"
            placeholder="ex: boost4life@..."
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={s?.passwordSet ? 'Parolă nouă (lasă gol ca s-o păstrezi)' : 'Parola'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={!canWrite}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={!canWrite}
            />
            <span>
              Activ (comenzile se sincronizează la 30 de minute; facturile de vânzare / cumpărare
              zilnic la 04:00 / 04:30)
            </span>
          </label>

          <div className="flex flex-wrap gap-2">
            {canWrite && (
              <>
                <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
                  Salvează
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  loading={testMutation.isPending}
                  disabled={!s?.passwordSet}
                >
                  Testează conexiunea
                </Button>
              </>
            )}
            {canSync && s?.passwordSet && s?.enabled && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => syncOrders.mutate()}
                  loading={syncOrders.isPending}
                >
                  Sincronizează comenzile
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => syncCustomerInv.mutate()}
                  loading={syncCustomerInv.isPending}
                >
                  Sincronizează facturile de vânzare
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => syncSupplierInv.mutate()}
                  loading={syncSupplierInv.isPending}
                >
                  Sincronizează facturile de cumpărare
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card description="Despre: eMAG emite facturi către B4L (comisioane, Genius, marketing — categorii FC/FCG/MKT/AVS), iar B4L emite facturi către clienții eMAG (seria DSMF, numerotare manuală). Reconcilierea se face în verificarea de conformitate.">
        <p className="text-xs text-b4l-text-muted">
          <strong>Limite de apeluri eMAG:</strong> 12 cereri/s pentru comenzi, 3 cereri/s pentru
          restul. Sincronizarea automată respectă limita.
        </p>
      </Card>
    </div>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
